// Analytics tracking wrappers (`<div onClick={trackNav(...)}>`) are
// presentational shells with `display: contents` that delegate all
// keyboard/mouse interaction to the inner SidebarItem. Each wrapper
// disables the relevant a11y rules inline at its own site rather than
// at file scope, so accessibility linting stays enabled everywhere else.
import { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import TodayIcon from '@material-ui/icons/Today';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
// import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ArriveLogo from './ArriveLogo';
import ArriveLogoIcon from './ArriveLogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import ArchitectureIcon from '@material-ui/icons/TextFormat';
import MapIcon from '@material-ui/icons/MyLocation';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyTeamsSidebarItem } from '@internal/backstage-plugin-team-management-frontend';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssessmentIcon from '@material-ui/icons/Assessment';
import SecurityIcon from '@material-ui/icons/Security';
import StorefrontIcon from '@material-ui/icons/Storefront';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import PieChartIcon from '@material-ui/icons/PieChart';
import StorageIcon from '@material-ui/icons/Storage';
import ExploreIcon from '@material-ui/icons/Explore';
import WebIcon from '@material-ui/icons/Web';
import { DeploymentInfo } from './DeploymentInfo';
import { FeatureFlagged } from '@backstage/core-app-api';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import { useLocation } from 'react-router-dom';
import { useMixpanelAnalytics } from '../../lib/analytics';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const useDeployedBadgeStyles = makeStyles({
  root: {
    padding: '6px 16px 10px',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.45)',
    lineHeight: 1.4,
    overflow: 'hidden',
  },
  branch: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

// PAT-929: the sidebar's own hover-expand timing is not reliably width- or
// delay-based in practice (confirmed by observation — the drawer sometimes
// expands to drawerWidthOpen on hover and sometimes doesn't, independent of
// window width), so a CSS breakpoint/media-query heuristic can't be trusted
// to track it. Instead, mirror the drawer's *actual* rendered width onto a
// CSS variable every time it changes, whatever the reason (hover, pin,
// anything future). my-custom-arrive-theme.css reads this variable for the
// page content's padding-left, so the two can never drift out of sync.
const SIDEBAR_DRAWER_WIDTH_CSS_VAR = '--sidebar-drawer-width';

const useSyncSidebarDrawerWidthCssVar = () => {
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;

    const drawer = document.querySelector<HTMLElement>(
      '[data-testid="sidebar-root"] > *',
    );
    if (!drawer) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        SIDEBAR_DRAWER_WIDTH_CSS_VAR,
        `${entry.contentRect.width}px`,
      );
    });
    observer.observe(drawer);
    return () => observer.disconnect();
  }, []);
};

// The drawer (`.drawer > * { flex-shrink: 0 }`) and SidebarScrollWrapper
// (`flex: 0 1 auto`) tie on specificity, so JSS injection order decides
// the winner; since the new frontend system it is the wrapper, which shrinks
// the middle area to ~1-3 visible items (Security unreachable). Inline style
// pins the intended behaviour: wrappers keep their content height and the
// drawer itself scrolls.
const noShrink = { flexShrink: 0 } as const;

const HIDDEN_BRANCHES = new Set(['master', 'main']);

const DeployedBranchBadge = () => {
  const classes = useDeployedBadgeStyles();
  const config = useApi(configApiRef);
  const { isOpen } = useSidebarOpenState();

  const branch = config.getOptionalString('app.deployedBranch');
  const pr = config.getOptionalString('app.deployedPr');

  if (!branch || HIDDEN_BRANCHES.has(branch) || !isOpen) return null;

  return (
    <Box className={classes.root}>
      {pr && <span>PR #{pr}</span>}
      <span className={classes.branch} title={branch}>
        {branch}
      </span>
    </Box>
  );
};

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        to="/home"
        underline="none"
        className={classes.link}
        aria-label="Home"
      >
        {isOpen ? <ArriveLogo /> : <ArriveLogoIcon />}
      </Link>
    </div>
  );
};

// Rendered by the app plugin's `app/nav` extension inside the stock
// `app/layout`, which supplies `SidebarPage`; this component renders only the
// `<Sidebar>`. Every entry is explicit rather than taken from the nav items.
export const SidebarContent = () => {
  const { track } = useMixpanelAnalytics();
  const location = useLocation();
  useSyncSidebarDrawerWidthCssVar();

  const trackNav = (itemName: string) => () => {
    try {
      track('sidebar_item_clicked', {
        item_name: itemName,
        current_page_path: location.pathname,
      });
    } catch (_e) {
      // silent
    }
  };

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        {/* PAT-1023: `@backstage/plugin-search`'s modal. Its `searchApiRef` comes
            from the alpha search plugin's `api:search`, and "View Full Results"
            resolves the package's route ref, bound to the alpha `page:search`
            (overridden by `searchModule`) — keep it on that ref, not a path. */}
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem
          icon={HomeIcon}
          to="/home"
          text="Home"
          onClick={trackNav('Home')}
        />
        <SidebarItem
          icon={TodayIcon}
          to="/my-day"
          text="My Day"
          onClick={trackNav('My Day')}
        />
        <SidebarItem
          icon={CategoryIcon}
          to="catalog"
          text="Catalog"
          end
          onClick={trackNav('Catalog')}
        />
        <MyTeamsSidebarItem
          singularTitle="My Team"
          pluralTitle="My Teams"
          icon={PeopleAltIcon}
          onClick={trackNav('My Team')}
        />
        <SidebarItem
          icon={AccountTreeIcon}
          to="teams"
          text="Teams"
          onClick={trackNav('Teams')}
        />
        <SidebarItem
          icon={ExtensionIcon}
          to="api-docs"
          text="APIs"
          onClick={trackNav('APIs')}
        />
        <SidebarItem
          icon={LibraryBooks}
          to="docs"
          text="Docs"
          end
          onClick={trackNav('Docs')}
        />
        {/* Disabled until Scaffolder with some useful Software Template is properly implemented
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          */}
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper
          data-testid="sidebar-middle-scroll-area"
          style={noShrink}
        >
          {/* Items in this group will be scrollable if they run out of space */}
          <SidebarItem
            icon={MapIcon}
            to="aoe-radar"
            text="Tech Radar"
            onClick={trackNav('Tech Radar')}
          />
          <SidebarItem
            icon={AssessmentIcon}
            to="analytics"
            text="Analytics"
            onClick={trackNav('Analytics')}
          />
          <FeatureFlagged with="fleet-census">
            <SidebarItem
              icon={StorageIcon}
              to="fleet-census"
              text="Fleet Census"
              onClick={trackNav('Fleet Census')}
            />
            <SidebarItem
              icon={PieChartIcon}
              to="tech-summary"
              text="Tech Summary"
              onClick={trackNav('Tech Summary')}
            />
            <SidebarItem
              icon={WebIcon}
              to="ui-repos"
              text="UI Repos"
              onClick={trackNav('UI Repos')}
            />
          </FeatureFlagged>
          <SidebarItem
            icon={SecurityIcon}
            to="security-insights"
            text="Security"
            onClick={trackNav('Security')}
          />
          <SidebarItem
            icon={StorefrontIcon}
            to="ai-marketplace"
            text="AI Marketplace"
            onClick={trackNav('AI Marketplace')}
          />
          <SidebarItem
            icon={CloudOffIcon}
            to="external-down-detector"
            text="External Down Detector"
            onClick={trackNav('External Down Detector')}
          />
          <FeatureFlagged with="code-migrations">
            <SidebarItem
              icon={SecurityIcon}
              to="code-migrations"
              text="Modernisation Tracker"
              onClick={trackNav('Modernisation Tracker')}
            />
          </FeatureFlagged>
        </SidebarScrollWrapper>
        <SidebarDivider />
        <SidebarScrollWrapper
          data-testid="sidebar-architecture-scroll-area"
          style={noShrink}
        >
          <SidebarItem
            icon={ArchitectureIcon}
            to="architecture"
            text="Architecture"
            onClick={trackNav('Architecture')}
          />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarScrollWrapper
        data-testid="sidebar-bottom-scroll-area"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          flexShrink: 0,
        }}
      >
        <SidebarSpace />
        <DeployedBranchBadge />
        <DeploymentInfo />
        <SidebarDivider />
        <SidebarItem
          icon={ExploreIcon}
          to="preview"
          text="Preview"
          onClick={trackNav('Preview')}
        />
      </SidebarScrollWrapper>
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarScrollWrapper
          data-testid="sidebar-settings-scroll-area"
          style={noShrink}
        >
          <SidebarSettings />
        </SidebarScrollWrapper>
      </SidebarGroup>
    </Sidebar>
  );
};
